/**
 * App 引导 Hook
 * 管理应用引导的状态和逻辑
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../lib/auth/AuthProvider';
import { 
  shouldShowAppOnboarding, 
  markAppOnboardingCompleted,
  resetAppOnboardingStatus,
  hasCompletedAppOnboarding
} from '../utils/appOnboardingUtils';
import { isNewUserForOnboarding } from '../utils/welcomeProjectUtils';

interface UseAppOnboardingReturn {
  /** 是否需要显示引导 */
  needsOnboarding: boolean;
  /** 是否为首次用户 */
  isFirstTime: boolean;
  /** 开始引导 */
  startOnboarding: () => void;
  /** 完成引导 */
  completeOnboarding: () => void;
  /** 跳过引导 */
  skipOnboarding: () => void;
  /** 重置引导状态（用于重新体验） */
  resetOnboarding: () => void;
  /** 是否已完成引导 */
  hasCompleted: boolean;
}

export const useAppOnboarding = (): UseAppOnboardingReturn => {
  const { user, loading } = useAuth();
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);

  // 检查引导状态
  useEffect(() => {
    if (loading || !user) {
      return;
    }

    const checkOnboardingStatus = () => {
      // 检查环境变量是否启用了应用引导功能
      const isOnboardingEnabled = import.meta.env.VITE_APP_ONBOARDING_ENABLED === 'true';

      if (!isOnboardingEnabled) {
        console.log('[useAppOnboarding] App onboarding is disabled by environment variable');
        setNeedsOnboarding(false);
        setIsFirstTime(false);
        setHasCompleted(true); // 设置为已完成，避免任何引导逻辑
        return;
      }

      const shouldShow = shouldShowAppOnboarding();
      const isNew = isNewUserForOnboarding(user);
      const completed = hasCompletedAppOnboarding();

      console.log('[useAppOnboarding] Checking onboarding status:', {
        shouldShow,
        isNew,
        completed,
        userId: user.id,
        isOnboardingEnabled
      });

      setNeedsOnboarding(shouldShow);
      setIsFirstTime(isNew);
      setHasCompleted(completed);
    };

    checkOnboardingStatus();
  }, [user, loading]);

  // 开始引导
  const startOnboarding = useCallback(() => {
    console.log('[useAppOnboarding] Starting onboarding');
    setNeedsOnboarding(true);
  }, []);

  // 完成引导
  const completeOnboarding = useCallback(() => {
    console.log('[useAppOnboarding] Completing onboarding');
    markAppOnboardingCompleted();
    setNeedsOnboarding(false);
    setHasCompleted(true);
  }, []);

  // 跳过引导
  const skipOnboarding = useCallback(() => {
    console.log('[useAppOnboarding] Skipping onboarding');
    markAppOnboardingCompleted();
    setNeedsOnboarding(false);
    setHasCompleted(true);
  }, []);

  // 重置引导状态
  const resetOnboarding = useCallback(() => {
    console.log('[useAppOnboarding] Resetting onboarding');
    resetAppOnboardingStatus();
    setNeedsOnboarding(true);
    setHasCompleted(false);
  }, []);

  return {
    needsOnboarding,
    isFirstTime,
    startOnboarding,
    completeOnboarding,
    skipOnboarding,
    resetOnboarding,
    hasCompleted
  };
};
